/**
 * Company technical identifiers — shared types, validation, and pure list
 * helpers. Neutral domain module imported by BOTH the CRM UI editor and the
 * Meridian tools/executors, so the shape + (system,label) upsert key + untrusted
 * jsonb parsing live in exactly one place.
 *
 * A company's `technical_identifiers` jsonb column is a STAFF-ONLY (support
 * engineers) extensible array of the technical system identifiers WTC needs to
 * service that client — Microsoft 365 tenant IDs, Azure subscription IDs, AWS
 * account IDs, Datadog org IDs, etc. ONE column, no migration to add a new
 * system type: each entry self-describes its {system, label, value, notes?}.
 *
 * NOT for secrets/credentials (no client secret, API key, or password) — these
 * are directory/account identifiers only.
 */

import { z } from "zod";

/** One technical system identifier a support engineer uses to service a company. */
export interface TechnicalIdentifier {
  /** System slug the identifier belongs to (e.g. "microsoft_365", "aws"). */
  system: string;
  /** Human label for the identifier within that system (e.g. "Tenant ID"). */
  label: string;
  /** The identifier value itself (e.g. the tenant UUID / account number). */
  value: string;
  /** Optional free-text note (scope, environment, caveat). */
  notes?: string;
}

/**
 * Advisory list of common system slugs — freeform is allowed, but surfacing
 * these in the tool descriptions + editor autocomplete nudges toward consistent
 * slugs so the same system isn't stored as "m365" and "microsoft365".
 */
export const COMMON_TECHNICAL_IDENTIFIER_SYSTEMS = [
  "microsoft_365",
  "azure",
  "aws",
  "google_workspace",
  "google_cloud",
  "cloudflare",
  "datadog",
  "okta",
  "duo",
  "meraki",
  "fortinet",
  "sophos",
] as const;

const nonEmpty = (field: string) => z.string().trim().min(1, `${field} is required`);

/** Validates a single identifier entry at the API/UI boundary. */
export const technicalIdentifierSchema = z.object({
  system: nonEmpty("system"),
  label: nonEmpty("label"),
  value: nonEmpty("value"),
  notes: z.string().trim().optional(),
});

export type TechnicalIdentifierInput = z.infer<typeof technicalIdentifierSchema>;

function normalizeEntry(entry: TechnicalIdentifierInput): TechnicalIdentifier {
  return {
    system: entry.system,
    label: entry.label,
    value: entry.value,
    // Drop empty notes so stored rows stay clean and equality is predictable.
    ...(entry.notes ? { notes: entry.notes } : {}),
  };
}

/** Parse a JSON string, returning `null` (never throwing) on invalid JSON. */
function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/**
 * Parse an untrusted jsonb value (the stored column) into a clean identifier
 * array. Non-arrays become []; malformed entries are dropped rather than
 * throwing, so one bad row never blocks reading or editing the rest.
 *
 * IMPORTANT — dual transport. The SAME `technical_identifiers` jsonb column
 * arrives here in two different runtime shapes depending on how it was read:
 *   • PostgREST (`.from("companies").select(...)`) returns a parsed JS array.
 *   • pg_graphql over Apollo (the Meridian get/set/remove executors) returns
 *     the column as a JSON *string* — e.g. `'[{"system":...}]'` — even though
 *     the codegen `JSON` scalar is (mis)typed `Record<string, any>`. Treating
 *     that string as "not an array" silently returned [], which made the read
 *     tool report count:0 AND made every set overwrite the whole array (it
 *     upserted onto []). So we JSON.parse a string transport before inspecting.
 */
export function parseTechnicalIdentifiers(raw: unknown): TechnicalIdentifier[] {
  const value = typeof raw === "string" ? safeJsonParse(raw) : raw;
  if (!Array.isArray(value)) return [];
  const out: TechnicalIdentifier[] = [];
  for (const item of value) {
    const parsed = technicalIdentifierSchema.safeParse(item);
    if (parsed.success) out.push(normalizeEntry(parsed.data));
  }
  return out;
}

/** Identity of an entry is (system, label), compared case-insensitively. */
function sameKey(
  a: { system: string; label: string },
  b: { system: string; label: string },
): boolean {
  return (
    a.system.trim().toLowerCase() === b.system.trim().toLowerCase() &&
    a.label.trim().toLowerCase() === b.label.trim().toLowerCase()
  );
}

/**
 * Upsert one identifier keyed by (system, label). Replaces an existing entry
 * with the same key (case-insensitive) or appends a new one. Returns the next
 * array and whether the entry was newly created (vs. updated in place).
 */
export function upsertTechnicalIdentifier(
  list: readonly TechnicalIdentifier[],
  entry: TechnicalIdentifierInput,
): { next: TechnicalIdentifier[]; created: boolean } {
  const normalized = normalizeEntry(entry);
  const idx = list.findIndex((e) => sameKey(e, normalized));
  if (idx === -1) return { next: [...list, normalized], created: true };
  const next = list.slice();
  next[idx] = normalized;
  return { next, created: false };
}

/**
 * Remove identifier(s) matching (system, label). Returns the next array and how
 * many entries were removed (0 = nothing matched).
 */
export function removeTechnicalIdentifier(
  list: readonly TechnicalIdentifier[],
  system: string,
  label: string,
): { next: TechnicalIdentifier[]; removed: number } {
  const key = { system, label };
  const next = list.filter((e) => !sameKey(e, key));
  return { next, removed: list.length - next.length };
}
